import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

// ✅ Danh sách email Admin tối cao — thêm/xóa email ở đây
export const SUPREME_ADMIN_EMAILS = [
  'geminipro1year@gmail.com',
  'nenmongvietnam.kcn@gmail.com',
  'nhabaophuonglinh@gmail.com',
];

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isSupremeAdmin: boolean;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSupremeAdmin, setIsSupremeAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setUser(firebaseUser);
        if (firebaseUser && firebaseUser.email) {
          // Kiểm tra xem có phải Supreme Admin không
          const isSupreme = SUPREME_ADMIN_EMAILS.includes(firebaseUser.email.toLowerCase());
          if (isSupreme) {
            setIsSupremeAdmin(true);
            setIsAdmin(true);
          } else {
            setIsSupremeAdmin(false);
            // Kiểm tra role admin trong Firestore (ưu tiên theo uid, fallback theo email)
            let adminFound = false;
            const uidDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (uidDoc.exists() && uidDoc.data().role === 'admin') {
              adminFound = true;
            } else {
              const emailDoc = await getDoc(doc(db, 'users', firebaseUser.email.toLowerCase()));
              if (emailDoc.exists() && emailDoc.data().role === 'admin') {
                adminFound = true;
              }
            }
            setIsAdmin(adminFound);
          }
        } else {
          setIsAdmin(false);
          setIsSupremeAdmin(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setIsAdmin(false);
        setIsSupremeAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, isSupremeAdmin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
